import * as React from 'react';
import { Button, Modal } from 'react-bootstrap';

export interface CustomHookFormModalProps<T> {
  title: string | undefined;
  content?: React.ReactNode;
  onSubmit?: (data: T) => void; // Accepts a function that takes an argument of type T
  onSubmitCustomDisplay?: string;
  onCancel?: () => void;
  modalSize?: string;
}

// Define a custom type for the modal ref that exposes show and hide methods
export interface ModalHandle {
  show: () => void;
  hide: () => void;
}

// Use React.forwardRef to correctly handle refs with ModalHandle type
const CustomHookFormModal = React.forwardRef<
  ModalHandle,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  CustomHookFormModalProps<any>
>(({ title, content, onSubmit, onCancel, onSubmitCustomDisplay }, ref) => {
  const [show, setShow] = React.useState(false);

  // Expose show and hide methods to the parent component
  React.useImperativeHandle(ref, () => ({
    show: () => setShow(true),
    hide: () => setShow(false)
  }));

  const handleClose = () => setShow(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = (data: any) => {
    if (onSubmit) {
      onSubmit(data);
    }
    handleClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleClose}>
        <Modal.Dialog>
          <Modal.Header closeButton>
            <Modal.Title>{title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{content}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onCancel || handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                // Assuming you gather the data here or pass it from the content
                const data = {
                  /* your data here, replace with actual data */
                };
                handleSubmit(data);
              }}
            >
              {onSubmitCustomDisplay ? onSubmitCustomDisplay : 'Submit'}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal>
    </>
  );
});

export default CustomHookFormModal;
